/**
 * fb-layout.ts — Pure tree auto-layout engine. Ported from flow-builder-layout.jsx.
 *
 * 2026-08-31 — a decision is centred over N subtrees rather than exactly two.
 * Everything else about the algorithm is unchanged: measure bottom-up, draw
 * top-down, return absolute rects the canvas positions nodes at.
 */
import { FB_NODE_W, FB_GAP_X, FB_SLOT, FB_DGAP, FB_ENDH, FB_TRIG_H, fbIsTerminal, fbNodeHeight } from "./fb-model";
import { fpLabel, fpLinkColor, fpStyle } from "./flow-paths";
import type { FBFlow, FBStep } from "./fb-model";

export interface NodeRect { step?: FBStep; trigger?: boolean; id?: string; x:number; y:number; w:number; h:number; }
export interface Link { from:{x:number;y:number}; to:{x:number;y:number}; color:string; d:string; }
/** `pathKey` names the branch this slot inserts into (null = the trunk);
 *  `pathIndex` is its position, which is what the connector colour follows. */
export interface SlotCtx { parentId:string|null; pathKey:string|null; pathIndex:number|null }
export interface Slot { id:string; x:number; y:number; ctx:SlotCtx; index:number; pulse?:boolean; }
/** `w` — the pill's measured width. Fixed-width label pills clipped anything
 *  longer than "YES", which a named third path always is. */
export interface BranchLabel { x:number; y:number; text:string; color:string; tint:string; w:number; }
export interface End { x:number; y:number; text:string; }
export interface FBLayout { nodes:NodeRect[]; links:Link[]; slots:Slot[]; labels:BranchLabel[]; ends:End[]; width:number; height:number; }

type Ctx = SlotCtx;

/** Label pill width for a branch name, at the canvas's 11px bold. */
const labelWidth = (text: string): number => Math.max(40, Math.round(String(text).length * 6.6) + 18);

function fbLinkPath(l: Omit<Link,"d">): string {
  const f=l.from, t=l.to;
  if (Math.abs(f.x-t.x)<0.6) return `M ${f.x} ${f.y} L ${t.x} ${t.y}`;
  const dy = Math.max(18,(t.y-f.y)*0.5);
  return `M ${f.x} ${f.y} C ${f.x} ${f.y+dy}, ${t.x} ${t.y-dy}, ${t.x} ${t.y}`;
}

interface Meas { empty?:boolean; els:any[]; steps:FBStep[]; ctx:Ctx; w:number; h:number; spineX:number; }
interface MeasDec extends Meas { kind:"decision"; step:FBStep; branches:Meas[]; childrenW:number; nodeH:number; }

type NH = (s: FBStep) => number;

function measList(steps: FBStep[], ctx: Ctx, nh: NH): Meas {
  const els: any[] = steps.map((s) => s.kind==="decision" ? measDecision(s, nh) : { kind:"node", step:s, w:FB_NODE_W, h:nh(s), spineX:FB_NODE_W/2 });
  if (els.length===0) return { empty:true, ctx, steps, w:FB_NODE_W, h:FB_SLOT+FB_ENDH, spineX:FB_NODE_W/2, els:[] };
  const spineX = Math.max(...els.map((e) => e.spineX));
  let w=0; els.forEach((e) => { w=Math.max(w,(spineX-e.spineX)+e.w); });
  let h=FB_SLOT; els.forEach((e,i) => { h+=e.h; if (!fbIsTerminal(steps[i])) h+=FB_SLOT; else if (steps[i].kind==="decision" && i<steps.length-1) h+=FB_SLOT; });
  const lastStep = steps[steps.length-1];
  if (!fbIsTerminal(lastStep)) h+=FB_ENDH;
  else if (lastStep.kind==="decision") h+=FB_SLOT; // continuation slot below a trailing decision (Join insertion point)
  return { els, steps, ctx, w, h, spineX };
}

function measDecision(s: FBStep, nh: NH): MeasDec {
  if (s.kind!=="decision") throw new Error("not decision");
  const branches = (s.paths ?? []).map((p, i) => measList(p.steps ?? [], { parentId:s.id, pathKey:p.key, pathIndex:i }, nh));
  const childrenW = branches.reduce((acc, b) => acc + b.w, 0) + FB_GAP_X * Math.max(0, branches.length - 1);
  const nodeH = nh(s);
  const w = Math.max(FB_NODE_W, childrenW);
  const tallest = branches.reduce((acc, b) => Math.max(acc, b.h), 0);
  return { kind:"decision", step:s, branches, childrenW, nodeH, w, spineX:w/2, h:nodeH+FB_DGAP+tallest, els:[], steps:[s], ctx:{ parentId:null, pathKey:null, pathIndex:null } };
}

/** opts (ADDITIVE — existing callers unaffected):
 *  - nodeHeight: override the per-step height (e.g. measured DOM heights so
 *    variable-length page nodes never overlap).
 *  - noTrigger: omit the trigger node — the flow starts at the first step. */
export function fbLayout(flow: FBFlow, opts?: { nodeHeight?: (s: FBStep) => number; noTrigger?: boolean }): FBLayout {
  const nh: NH = opts?.nodeHeight ?? fbNodeHeight;
  const nodes: NodeRect[] = [], links: Omit<Link,"d">[] = [], slots: Slot[] = [], labels: BranchLabel[] = [], ends: End[] = [];

  /** Draws a step list; returns the list's OPEN TAILS — the points where a
   *  path falls through without an End step. When `joined` is true those
   *  tails are handed up to be connected into a Join instead of rendering a
   *  "Flow ends" hint. */
  function drawList(blk: Meas, ax: number, ay: number, topFromY: number|null, noTop?: boolean, joined?: boolean): {x:number;y:number}[] {
    const sx = ax+blk.spineX;
    const slotId = (i: number) => `sl|${blk.ctx.parentId||"root"}|${blk.ctx.pathKey||"-"}|${i}`;
    const inColor = fpLinkColor(blk.ctx.pathIndex);
    if (blk.empty) {
      slots.push({ id:slotId(0), x:sx, y:ay+FB_SLOT/2, ctx:blk.ctx, index:0, pulse:!blk.ctx.parentId });
      if (topFromY!=null) links.push({ from:{x:sx,y:topFromY}, to:{x:sx,y:ay+FB_SLOT/2}, color:inColor });
      if (joined) return [{ x:sx, y:ay+FB_SLOT/2+13 }];
      ends.push({ x:sx, y:ay+FB_SLOT+FB_ENDH/2, text:blk.ctx.parentId?"":"Flow ends" });
      return [];
    }
    let y=ay;
    let prevBottom: number;
    let pendingTails: {x:number;y:number}[] | null = null;
    let openTails: {x:number;y:number}[] = [];
    if (noTop) {
      // No lead-in slot/connector — the flow starts AT the first node.
      prevBottom = y;
    } else {
      slots.push({ id:slotId(0), x:sx, y:y+FB_SLOT/2, ctx:blk.ctx, index:0 });
      if (topFromY!=null) links.push({ from:{x:sx,y:topFromY}, to:{x:sx,y:y+FB_SLOT/2}, color:inColor });
      prevBottom=y+FB_SLOT/2;
      y+=FB_SLOT;
    }
    blk.els.forEach((e: any, i: number) => {
      const ex=ax+blk.spineX-e.spineX;
      if (pendingTails) {
        // Previous step was a decision whose open branch tails merge into THIS
        // step (a Join): one curved link per tail, no straight spine link.
        pendingTails.forEach((tl) => links.push({ from:tl, to:{x:sx,y}, color:"#CDD3DD" }));
        pendingTails = null;
      } else {
        links.push({ from:{x:sx,y:prevBottom}, to:{x:sx,y}, color:"#CDD3DD" });
      }
      if (e.kind==="decision") {
        const isLastEl = i===blk.els.length-1;
        // Branches fall through when something follows (a Join) or when this
        // whole list is itself joined at an enclosing level.
        const decJoined = !isLastEl || !!joined;
        const tails = drawDecision(e,ex,y,decJoined);
        const bottom=y+e.h;
        if (!isLastEl) { pendingTails = tails; prevBottom=bottom; y=bottom+FB_SLOT; }
        else {
          // Trailing decision: a continuation slot below the subtree (the Join
          // insertion point). No connector — branches carry their own ends.
          slots.push({ id:slotId(i+1), x:sx, y:bottom+FB_SLOT/2, ctx:blk.ctx, index:i+1 });
          prevBottom=bottom; y=bottom;
          if (joined) openTails = tails;
        }
        return;
      }
      nodes.push({ step:e.step, x:ex, y, w:FB_NODE_W, h:e.h });
      const bottom=y+e.h;
      if (!fbIsTerminal(blk.steps[i])) {
        slots.push({ id:slotId(i+1), x:sx, y:bottom+FB_SLOT/2, ctx:blk.ctx, index:i+1 });
        links.push({ from:{x:sx,y:bottom}, to:{x:sx,y:bottom+FB_SLOT/2}, color:"#CDD3DD" });
        prevBottom=bottom+FB_SLOT/2; y=bottom+FB_SLOT;
      } else { prevBottom=bottom; y=bottom; }
    });
    const isRoot=!(blk.ctx&&blk.ctx.parentId);
    const lastStep=blk.steps[blk.steps.length-1];
    if (!fbIsTerminal(lastStep)) {
      if (joined) return [{ x:sx, y:prevBottom }];
      links.push({ from:{x:sx,y:prevBottom}, to:{x:sx,y:y+FB_ENDH/2}, color:"#CDD3DD" });
      ends.push({ x:sx, y:y+FB_ENDH/2, text:isRoot?"Flow ends":"" });
    }
    return openTails;
  }

  /** Centres the decision node over ALL its branch subtrees and lays them out
   *  left to right in path order. Two branches produce the exact geometry the
   *  two-field version did. */
  function drawDecision(e: MeasDec, ax: number, ay: number, joined?: boolean): {x:number;y:number}[] {
    const s=e.step;
    if (s.kind!=="decision") return [];
    const nodeX=ax+e.spineX-FB_NODE_W/2;
    nodes.push({ step:s as FBStep, x:nodeX, y:ay, w:FB_NODE_W, h:e.nodeH });
    const branchY=ay+e.nodeH+FB_DGAP;
    const fromPt={x:ax+e.spineX,y:ay+e.nodeH};
    let x=ax+e.spineX-e.childrenW/2;
    let tails: {x:number;y:number}[] = [];
    e.branches.forEach((b, i) => {
      const bx=x;
      const spine=bx+b.spineX;
      const st=fpStyle(i);
      const text=fpLabel(s.paths[i]!);
      labels.push({ x:spine, y:ay+e.nodeH+22, text, color:st.color, tint:st.tint, w:labelWidth(text) });
      links.push({ from:fromPt, to:{x:spine,y:branchY}, color:st.color });
      tails = tails.concat(drawList(b, bx, branchY, null, false, joined));
      x += b.w + FB_GAP_X;
    });
    return tails;
  }

  const root=measList(flow.steps||[], { parentId:null, pathKey:null, pathIndex:null }, nh);
  const spineX=Math.max(FB_NODE_W/2,root.spineX);
  const trigX=spineX-FB_NODE_W/2, rootX=spineX-root.spineX;
  const width=Math.max(trigX+FB_NODE_W, rootX+root.w);
  let height: number;
  if (opts?.noTrigger) {
    // The flow starts AT the first step — no trigger node and no add-slot above it.
    drawList(root, rootX, 6, null, true);
    height=root.h+16;
  } else {
    nodes.push({ trigger:true, id:"trigger", x:trigX, y:0, w:FB_NODE_W, h:FB_TRIG_H });
    drawList(root, rootX, FB_TRIG_H, FB_TRIG_H-6);
    height=FB_TRIG_H+root.h+10;
  }
  const finalLinks: Link[] = links.map((l) => ({ ...l, d:fbLinkPath(l) }));
  return { nodes, links:finalLinks, slots, labels, ends, width, height };
}
